var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { BaseTerrainProvider } from "./BaseTerrainProvider";
import { Rectangle } from "../../math";
/**
 *
 */
export class LayeredTerrainProvider extends BaseTerrainProvider {
    constructor(layers) {
        super();
        this.layers = [...layers];
        if (this.layers.length === 0) {
            throw new Error("Created a LayeredTerrainProvider with 0 layers");
        }
        this._tilingScheme = this.layers[0].tilingScheme;
        this.layers.forEach((v) => {
            if (v.tilingScheme.constructor !== this._tilingScheme.constructor) {
                throw new Error("All layers in LayeredTerrainProvider must use the same tiling scheme");
            }
        });
        // The globally minimum zoom level of the combined dataset is simply the lowest zoom level of any layer
        this._minLevel = Math.min(...this.layers.map((v) => v.minLevel));
        // The globally maximum zoom level of the combined dataset is simply the highest zoom level of any layer
        this._maxLevel = Math.max(...this.layers.map((v) => v.maxLevel));
        // Find the union of all layers
        const layersRegion = this.layers
            .map((v) => v.region)
            .reduce((p, c, i) => {
            return p.union(c, p);
        }, new Rectangle());
        // Constrain the region to be within the tiling scheme
        const region = layersRegion.intersection(this._tilingScheme.rectangle);
        // If the output region is not legal it is very likely that
        if (!region.isLegal())
            throw new Error("Layered region is not legal, invalid layer rectangles received");
        this._region = region;
    }
    get tilingScheme() {
        return this._tilingScheme;
    }
    get region() {
        return this._region;
    }
    get attributions() {
        return this.layers.flatMap((v) => v.attributions);
    }
    get minLevel() {
        return this._minLevel;
    }
    get maxLevel() {
        return this._maxLevel;
    }
    loadTile(x, y, zoomLevel) {
        return __awaiter(this, void 0, void 0, function* () {
            // Start a load request for all tiles concurrently.
            const promises = [];
            for (const layer of this.layers) {
                promises.push(layer.loadTile(x, y, zoomLevel));
            }
            // Wait for all the promises to complete, successfully or otherwise
            const results = yield Promise.allSettled(promises);
            // Flatten all result arrays into a single array and return it
            const out = [];
            for (const result of results) {
                if (result.status === "fulfilled") {
                    out.push(...result.value);
                }
            }
            return out;
        });
    }
    hasDataForTile(x, y, zoomLevel) {
        // Only a single layer has to provide data
        for (const layer of this.layers) {
            if (layer.hasDataForTile(x, y, zoomLevel)) {
                return true;
            }
        }
        return false;
    }
    canRefine(x, y, zoomLevel) {
        // Only a single layer must have refinement available
        for (const layer of this.layers) {
            if (layer.canRefine(x, y, zoomLevel)) {
                return true;
            }
        }
        return false;
    }
}
